import { useMemo } from 'react';

import useFetchFilteredResults, { SearchQueryParameter } from 'js/hooks/use-fetch-filtered-results';

import ContentContainer from 'components/content-container/ContentContainer';
import EmptyList from 'components/empty-list/EmptyList';
import FilterLayout from 'components/filter-layout/FilterLayout';
import Form from 'components/form/Form';
import Search from 'components/search/Search';
import Pagination from 'components/pagination/Pagination';
import SearchResult from 'components/search-result/SearchResult';
import Spinner from 'components/spinner/Spinner';

import { ISearchPage } from 'source/NFR.types';

type SearchPageFilterState = Pick<
  ISearchPage,
  'results' | 'pagination' | 'resultDescription' | 'emptyList' | 'search' | 'filterLayout'
>;

const SearchPage: React.FC<ISearchPage> = ({
  emptyList,
  filterLayout,
  form,
  pagination,
  results = [],
  resultDescription,
  search,
  title,
  fetchFilteredResultsEndpoint
}) => {
  // The search API endpoint requires a "q" parameter. Send it even if empty.
  const searchDefaults = useMemo<SearchQueryParameter>(
    () => ({
      q: typeof search?.input?.value === 'string' ? search.input.value : '',
      subjects: [],
      targetGroups: [],
      type: [],
      categories: []
    }),
    [search?.input?.value]
  );

  const [
    isLoading,
    {
      results: resultsState,
      pagination: paginationState,
      resultDescription: resultDescriptionState,
      emptyList: emptyListState,
      search: searchState,
      filterLayout: filterLayoutState
    },
    fetchResults
  ] = useFetchFilteredResults<SearchPageFilterState>(
    {
      results,
      pagination,
      resultDescription,
      emptyList,
      search,
      filterLayout
    },
    fetchFilteredResultsEndpoint,
    form.endpoint,
    undefined,
    searchDefaults
  );

  // A new search starts from a clean slate. Reset filters when a new search is submitted (new query)
  const handleNewSearch = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const queryName = search?.input?.name;
    const rawQuery = queryName ? new FormData(event.currentTarget).get(queryName) : null;
    const query = typeof rawQuery === 'string' ? rawQuery : '';
    const url =
      queryName && query ? `${form.endpoint}?${new URLSearchParams({ [queryName]: query })}` : form.endpoint;
    window.location.assign(url);
  };

  const topLine = <div className="search-page--results">{resultDescriptionState}</div>;

  return (
    <Form className="search-page" showSubmitButton={false} {...form} onSubmit={handleNewSearch}>
      <ContentContainer>
        {title && <h1 className="search-page--title">{title}</h1>}
        <Search {...searchState} />
      </ContentContainer>
      <FilterLayout
        {...filterLayoutState}
        topContent={topLine}
        fetchFilteredResults={fetchResults}
        isLoading={isLoading}
      >
        <>
          {resultsState && resultsState.length > 0 ? (
            resultsState?.map((result, index) => {
              return <SearchResult key={result.title + String(index)} {...result} />;
            })
          ) : (
            <EmptyList {...emptyListState} />
          )}
          <Spinner isActive={isLoading} />
        </>
        <Pagination {...paginationState} />
      </FilterLayout>
    </Form>
  );
};

SearchPage.displayName = 'SearchPage';

export default SearchPage;
